/**
 * BaseWiringCommand.js
 * Tüm wiring komutları için ortak base class
 *
 * Bu class, Socket, LightSwitch, LightFixture, Machine gibi
 * component + feeder ekleyen komutlardaki ortak kodu içerir.
 *
 * Pattern:
 * 1. Component (socket/switch/fixture/machine) oluşturulur
 * 2. Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine component ve wire eklenir
 * 4. SceneObjectCache'e eklenir (O(1) lookup için)
 */

import BaseCommand from './BaseCommand';
import { addComponent, addFeeder, addLoadToComponent, removeComponent, removeFeeder } from '../../../redux/simpleSupplySlice';
import * as THREE from 'three';
import sceneObjectCache from '../SceneObjectCache';
import { batch } from 'react-redux';

class BaseWiringCommand extends BaseCommand {
  /**
   * @param {string} commandType - Komut tipi (örn: 'ADD_SOCKET', 'DELETE_LIGHT_SWITCH')
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} componentData - Component verisi
   * @param {object} feederData - Feeder verisi
   * @param {object} sceneData - Three.js scene verisi
   * @param {object} config - Alt sınıfa özgü konfigürasyon
   */
  constructor(commandType, action, componentData, feederData, sceneData, config = {}) {
    super(commandType, {
      before: action === 'DELETE' ? { component: componentData, feeder: feederData } : null,
      after: action === 'ADD' ? { component: componentData, feeder: feederData } : null
    }, sceneData);

    this.action = action; // 'ADD' veya 'DELETE'
    this.config = config; // Alt sınıfa özgü konfigürasyon (componentKey, cacheMethodPrefix, etc.)
  }

  /**
   * Component + Feeder ekleme/silme işlemini gerçekleştir
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla (performans için)
   */
  execute(scene, dispatch, skipRedux = false) {
    if (this.action === 'ADD') {
      this._addComponent(scene, dispatch, skipRedux);
    } else if (this.action === 'DELETE') {
      this._deleteComponent(scene, dispatch, skipRedux);
    }
  }

  /**
   * Component + Feeder ekleme/silme işlemini geri al
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla (performans için)
   */
  undo(scene, dispatch, skipRedux = false) {
    if (this.action === 'ADD') {
      // ADD işlemini geri al = DELETE yap
      this._deleteComponent(scene, dispatch, skipRedux);
    } else if (this.action === 'DELETE') {
      // DELETE işlemini geri al = ADD yap
      this._addComponent(scene, dispatch, skipRedux);
    }
  }

  /**
   * Sahneye Component + Feeder ekle
   * Alt sınıflar createSymbol() metodunu implement etmeli
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla
   */
  _addComponent(scene, dispatch, skipRedux = false) {
    const { component: componentData, feeder: feederData } = this.data.after || this.data.before;

    // 1-2. Redux'a component + feeder ekle (skipRedux=false ise)
    // ✅ YENİ: addLoadToComponent kullan - normal oluşturma ile AYNI fonksiyon
    // Bu sayede power propagation, exitOrder hesaplama vb. tutarlı olur
    if (!skipRedux) {
      if (componentData && componentData.parentComponentId) {
        // ✅ Load component'i olan hatlar için addLoadToComponent kullan
        // Bu, useSocket/useLighting/useMachines ile aynı yolu kullanır
    
        dispatch(addLoadToComponent({
          parentComponentId: componentData.parentComponentId,
          loadData: componentData,
          feederData: feederData,
          updateExisting: false // REDO'da her zaman yeni oluşturma
        }));
      } else {
 
        batch(() => {
          if (componentData) {
            dispatch(addComponent(componentData));
          }
          if (feederData && feederData.id) {
            dispatch(addFeeder(feederData));
          }
        });
      }
    }

    // 3. Three.js sahnesine component ekle
    // ⚠️ componentData null olabilir (branch update durumu)
    if (componentData && componentData.id) {
      const componentGroup = this.createSymbol(scene, componentData, this.sceneData);

      if (componentGroup) {
        // userData'yı set et
        componentGroup.userData = {
          ...componentData, // Redux component data
          ...componentGroup.userData, // Symbol creation'dan gelen alanlar (basePoint, power, etc.)
          type: this.config.componentType,
          id: componentData.id,
          isElectrical: true
        };

        // Sahneye ekle
        scene.add(componentGroup);

        // ✅ Cache'e ekle (O(1) lookup için)
        this.addToCache(componentData.id, componentGroup);
      }
    }

    // 4. Three.js sahnesine wire (feeder) ekle
    // ⚠️ SADECE global undo/redo için wire oluştur (skipRedux=false)
    // Local undo sırasında (çizim sırasında) wire oluşturma - tempLine zaten var
    // ✅ FIX: wireVertices VEYA vertices kullan (Socket/Lighting uyumluluğu)
    // ⚠️ feederData null olabilir (branch update durumu)
    const wireVerts = this.sceneData?.wireVertices || this.sceneData?.vertices;
    if (!skipRedux && this.shouldCreateWire() && feederData && feederData.id && wireVerts && wireVerts.length > 1) {
      // sceneData'ya wireVertices ekle (createWire için)
      const enhancedSceneData = { ...this.sceneData, wireVertices: wireVerts };
      this.wireLine = this.createWire(feederData, enhancedSceneData, componentData);

      if (this.wireLine) {
        scene.add(this.wireLine);

        // ✅ Wire'ı cache'e ekle
        sceneObjectCache.addFeeder(feederData.id, this.wireLine);
      }
    }
  }

  /**
   * Sahneden Component + Feeder sil
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla (performans için)
   */
  _deleteComponent(scene, dispatch, skipRedux = false) {
    const { component: componentData, feeder: feederData } = this.data.after || this.data.before;

 

    // 1-2. Redux'tan component + feeder sil (skipRedux=false ise)
    if (!skipRedux) {
      batch(() => {
        if (componentData && componentData.id) {
          dispatch(removeComponent(componentData.id));
        }
        // ✅ feederData null/undefined ise feeder silme (branch update durumu)
        if (feederData && feederData.id) {
          dispatch(removeFeeder(feederData.id));
        }
      });
    }

    // 3. Three.js sahnesinden component sil - ✅ Cache'den al (traverse yerine)
    // ⚠️ componentData null olabilir (branch update durumu)
    if (componentData && componentData.id) {
      const componentToRemove = this.getFromCache(componentData.id);

      if (componentToRemove) {
        componentToRemove.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });

        if (componentToRemove.parent) {
          componentToRemove.parent.remove(componentToRemove);
        } else {
          scene.remove(componentToRemove);
        }

        // ✅ Cache'den sil
        this.removeFromCache(componentData.id);
      }
    }

    // 4. Three.js sahnesinden wire sil - ✅ Cache'den al (traverse yerine)
    // ⚠️ SADECE global undo/redo için wire sil (skipRedux=false)
    // Local undo sırasında wire silinmemeli - tempLine zaten çizimi yönetiyor
    if (!skipRedux && feederData?.id) {
      const wireToRemove = sceneObjectCache.getFeeder(feederData.id);

      if (wireToRemove) {
        if (wireToRemove.geometry) wireToRemove.geometry.dispose();
        if (wireToRemove.material) wireToRemove.material.dispose();
        scene.remove(wireToRemove);

        // ✅ Cache'den sil
        sceneObjectCache.removeFeeder(feederData.id);
      }
    }
  }

  /**
   * Wire çizgisi oluştur
   * Alt sınıflar override edebilir (örn: curved wire için)
   */
  createWire(feederData, sceneData, componentData) {
    const wireVertices = sceneData.wireVertices.map(v => new THREE.Vector3(v.x, v.y, v.z));

    const finalGeometry = new THREE.BufferGeometry().setFromPoints(wireVertices);
    const wireColor = this.getWireColor();
    const finalMaterial = new THREE.LineBasicMaterial({ color: wireColor });
    const wireLine = new THREE.Line(finalGeometry, finalMaterial);

    // ✅ Component ID'sini socket listesine ekle (Gizmo sürükleme için)
    const socketIds = componentData ? [componentData.id] : [];
    const socketIdsMap = componentData ? { [componentData.id]: true } : {};

    // ✅ Wire Marker ID'leri için boş listeler
    const wireMarkerIds = [];
    const wireMarkerIdsMap = {};

    // ✅ wireID kullan (varsa), yoksa feederId kullan (backward compat)
    const wireId = feederData.wireID || feederData.id;

    // ✅ PanelID: Panel çıkış hatları için gerekli (panelExitManager tarafından kullanılır)
    const panelID = feederData.panelId || feederData.sourceId || sceneData?.panelID || null;

    wireLine.userData = {
      id: wireId, // ✅ wireID kullan (wire.userData.id = wireID olmalı)
      type: 'feederWire',
      feederId: feederData.id,
      sourceId: feederData.sourceId || feederData.panelId, // ✅ Add sourceId for gizmo swapping logic
      panelID: panelID, // ✅ Panel exit yönetimi için (updatePanelWiringPositions)
      // ✅ LoadId'ler - panelExitManager wire eşleştirmesi için gerekli
      lightingLoadId: feederData.lightingLoadId || sceneData?.lightingLoadId || null,
      machineLoadId: feederData.machineLoadId || sceneData?.machineLoadId || null,
      socketLoadId: feederData.socketLoadId || sceneData?.socketLoadId || null,
      loadId: componentData?.id || null, // Generic loadId
      wireID: feederData.wireID || sceneData?.wireID || null, // ✅ wireID (buat için)
      sockets: socketIds, // ✅ Gizmo için socket listesi
      socketIdsMap: socketIdsMap, // ✅ Gizmo için socket map
      wireMarkerIds: wireMarkerIds, // ✅ Wire Marker ID listesi
      wireMarkerIdsMap: wireMarkerIdsMap, // ✅ Wire Marker ID map
      isSelectable: true
    };

    return wireLine;
  }

  // ========================================
  // Alt sınıfların implement etmesi gereken metodlar
  // ========================================

  /**
   * Component sembolünü oluştur
   * Alt sınıflar MUTLAKA implement etmeli
   * @returns {THREE.Group} Component group
   */
  createSymbol(scene, componentData, sceneData) {
    throw new Error('createSymbol() metodu alt sınıfta implement edilmeli');
  }

  /**
   * Wire rengi
   * Alt sınıflar override edebilir
   */
  getWireColor() {
    return this.config.wireColor || 0x00ff00;
  }

  /**
   * Wire oluşturulmalı mı?
   * Alt sınıflar override edebilir (örn: FixtureCommand false döner)
   */
  shouldCreateWire() {
    return this.config.createWire !== false;
  }

  /**
   * Cache'e component ekle
   * Alt sınıflar override edebilir
   */
  addToCache(componentId, componentGroup) {
    const method = `add${this.config.cacheMethodSuffix}`;
    if (sceneObjectCache[method]) {
      sceneObjectCache[method](componentId, componentGroup);
    }
  }

  /**
   * Cache'den component al
   * Alt sınıflar override edebilir
   */
  getFromCache(componentId) {
    const method = `get${this.config.cacheMethodSuffix}`;
    if (sceneObjectCache[method]) {
      return sceneObjectCache[method](componentId);
    }
    return null;
  }

  /**
   * Cache'den component sil
   * Alt sınıflar override edebilir
   */
  removeFromCache(componentId) {
    const method = `remove${this.config.cacheMethodSuffix}`;
    if (sceneObjectCache[method]) {
      sceneObjectCache[method](componentId);
    }
  }
}

export default BaseWiringCommand;
